import React from 'react';
import { Link } from 'react-router-dom';
import { BADGES } from '../utils/constants';
import './BoutCard.css';

/**
 * Reusable bout card component.
 * Renders differently based on context (lobby, my bouts, open, invitations).
 */
export default function BoutCard({
  bout,
  currentUid,
  onJoin,
  onDelete,
  onAccept,
  onDecline,
  actionLoading,
}) {
  const participants = Object.entries(bout.participants || {}).map(([uid, data]) => ({
    uid,
    ...data,
  }));

  const creator = participants.find((p) => p.uid === bout.createdBy);
  const opponent = participants.find((p) => p.uid !== bout.createdBy);

  const isCreator = bout.createdBy === currentUid;
  const isParticipant = bout.participantUids?.includes(currentUid);
  const isInvited = bout.invitedUid === currentUid;

  const statusLabel = bout.status === 'active' ? 'Active' : bout.status === 'completed' ? 'Completed' : 'Open';

  return (
    <div className={`bout-card bout-card-${bout.status}`}>
      <div className="bout-card-top">
        <div className="bout-card-players">
          {/* Creator */}
          <div className="bout-card-player">
            <span className="bout-card-badge">
              {BADGES[creator?.activeBadge]?.emoji || '🐣'}
            </span>
            <Link to={`/users/${creator?.username}`} className="bout-card-username">
              {creator?.username || 'Unknown'}
            </Link>
            {bout.status !== 'open' && (
              <span className="bout-card-streak">🔥 {creator?.boutStreak || 0}</span>
            )}
          </div>

          <span className="bout-card-vs">vs</span>

          {/* Opponent */}
          <div className="bout-card-player">
            {opponent ? (
              <>
                <span className="bout-card-badge">
                  {BADGES[opponent.activeBadge]?.emoji || '🐣'}
                </span>
                <Link to={`/users/${opponent.username}`} className="bout-card-username">
                  {opponent.username}
                </Link>
                {bout.status !== 'open' && (
                  <span className="bout-card-streak">🔥 {opponent.boutStreak || 0}</span>
                )}
              </>
            ) : (
              <span className="bout-card-waiting">
                {bout.visibility === 'private'
                  ? `Invited: ${bout.invitedUsername || '...'}`
                  : 'Waiting for opponent...'}
              </span>
            )}
          </div>
        </div>

        <div className="bout-card-meta">
          <span className={`bout-card-status bout-status-${bout.status}`}>
            {statusLabel}
          </span>
          <span className="bout-card-target">First to {bout.targetStreak}</span>
          {bout.visibility === 'private' && (
            <span className="bout-card-visibility">Private</span>
          )}
        </div>
      </div>

      {/* Winner banner */}
      {bout.status === 'completed' && bout.winnerUsername && (
        <div className="bout-card-winner">
          Winner: {bout.winnerUsername}
        </div>
      )}

      {/* Progress bars for active bouts */}
      {bout.status === 'active' && (
        <div className="bout-card-progress-wrap">
          {participants.map((p) => (
            <div key={p.uid} className="bout-card-progress-row">
              <span className="bout-progress-name">{p.username}</span>
              <div className="bout-progress-bar">
                <div
                  className="bout-progress-fill"
                  style={{ width: `${Math.min(100, ((p.boutStreak || 0) / bout.targetStreak) * 100)}%` }}
                />
              </div>
              <span className="bout-progress-count">{p.boutStreak || 0}/{bout.targetStreak}</span>
            </div>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div className="bout-card-actions">
        {/* View standings for active/completed bouts the user is in */}
        {isParticipant && bout.status !== 'open' && (
          <Link to={`/arena/bouts/${bout.id}`} className="btn-bout-view">
            View Standings
          </Link>
        )}

        {/* Join button for lobby bouts */}
        {onJoin && !isParticipant && !isCreator && bout.status === 'open' && (
          <button
            className="btn-bout-join"
            onClick={() => onJoin(bout.id)}
            disabled={actionLoading}
          >
            {actionLoading ? '...' : 'Join Bout'}
          </button>
        )}

        {/* Delete button for open bouts by creator */}
        {onDelete && isCreator && bout.status === 'open' && (
          <button
            className="btn-bout-delete"
            onClick={() => onDelete(bout.id)}
            disabled={actionLoading}
          >
            {actionLoading ? '...' : 'Delete'}
          </button>
        )}

        {/* Accept/Decline for invitations */}
        {isInvited && bout.status === 'open' && (
          <>
            {onAccept && (
              <button
                className="btn-bout-accept"
                onClick={() => onAccept(bout.id)}
                disabled={actionLoading}
              >
                {actionLoading ? '...' : 'Accept'}
              </button>
            )}
            {onDecline && (
              <button
                className="btn-bout-decline"
                onClick={() => onDecline(bout.id)}
                disabled={actionLoading}
              >
                {actionLoading ? '...' : 'Decline'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
